/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Component, Input, ElementRef, Renderer, ViewEncapsulation } from '@angular/core';
import { OnInit, OnDestroy, HostBinding, HostListener, Host, Optional, forwardRef, Inject } from '@angular/core';
import KeyCode from '../util/key.code';
import classNames from 'classnames';

import { Menu } from './menu';
import { SubMenu } from './submenu';
/**
 * Menu导航菜单
 * 参考: https://github.com/react-component/menu
 */
export class MenuItem implements OnInit, OnDestroy {
public isMenuItem = 1;
public isSubMenu = 0;

     title: string;

    // tslint:disable-next-line:no-input-rename
     eventKey: string;

     disabled = false;
/**
 * 是否允许多选，默认为false
 */
multiple?= false;
/**
 * 模式的菜单缩进宽度，默认为24px
 */
inlineIndent = 24;
/**
 * 用户CSS样式
 */
class: string;
/**
 * 展开/关闭菜单时的动画效果
 */
openAnimation?: string | Object;
     openTransitionName?: string | Object;

     role = 'menuitem';
public open = false;

    protected level: number;
/**
 * 鼠标移出的一定时间后(如30毫秒)，再执行隐藏子菜单的操作
 */
private mouseLeaveTimer: any;
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} parentMenu
 * @param {?} submenu
 */
constructor(
private renderer: Renderer,
private elementRef: ElementRef,
private parentMenu: Menu,
private submenu: SubMenu) {
        if (parentMenu !== null) {
            parentMenu.registerItem(this);
        }
        if (submenu !== null) {
            submenu.registerItem(this);
        }
    }
/**
 * @return {?}
 */
get active(): boolean {
        return !this.disabled && this.parentMenu.activeKey === this.eventKey;
    }
/**
 * @return {?}
 */
get mode(): string {
        if (this.submenu) {
            return this.submenu.mode === 'horizontal' ? 'vertical' : this.submenu.mode;
        }
        return null;
    }
/**
 * 样式前缀
 * @return {?}
 */
get prefixCls(): string {
        return this.parentMenu && this.parentMenu.prefixCls || '';
    }
/**
 * @return {?}
 */
ngOnInit(): void {
        if (this.submenu !== null) {
            this.level = this.submenu.level + 1;
        }
        if (!this.eventKey && this.parentMenu) {
            const /** @type {?} */ prefix = this.submenu && this.submenu.eventKey || 'menu';
            this.eventKey = this.parentMenu.getGUID(prefix);
        }

        if (this.parentMenu) {
            // 动画效果继承父菜单
            if (!this.openTransitionName) {
                this.openTransitionName = this.parentMenu.getOpenAnimationName();
            }
        }
    }
/**
 * @return {?}
 */
ngOnDestroy(): void {
        const /** @type {?} */ parentMenu: Menu = this.parentMenu;
        this.clearMouseLeaveTimer();
        if (parentMenu) {
            parentMenu.onDestroy(this.eventKey);
        }
    }
/**
 * 菜单样式
 * @return {?}
 */
get classes(): string {
        const /** @type {?} */ classes = classNames(`${this.prefixCls}-item`, {
            [`${this.prefixCls}-item-active`]: !this.disabled && this.active,
            [`${this.prefixCls}-item-selected`]: this.isSelected(),
            [`${this.prefixCls}-item-disabled`]: this.disabled
        }, this.class);
        return classes;
    }
/**
 * @return {?}
 */
get paddingLeft(): string {
        if (this.mode === 'inline') {
            return (this.inlineIndent * this.level) + 'px';
        }
        return null;
    }
/**
 * @return {?}
 */
get ariaselected(): any {
        return this.isSelected() ? 'true' : null;
    }
/**
 * @return {?}
 */
get ariadisabled(): any {
        return this.disabled ? 'true' : null;
    }
/**
 * @param {?} event
 * @return {?}
 */
onClick(event: Event): void {
        if (this.disabled) {
            return;
        }

        const /** @type {?} */ selected = this.isSelected();
        const /** @type {?} */ eventKey = this.eventKey;
        const /** @type {?} */ keyPath: string[] = this.getKeyPath();
        const /** @type {?} */ info = {
            key: eventKey,
            keyPath: keyPath,
            item: this,
            domEvent: event
        };

        const /** @type {?} */ parentMenu: Menu = this.parentMenu;
        if (parentMenu) {
            parentMenu.onClick(info);

            if (this.multiple) {
                if (selected) {
                    parentMenu.onDeselect(info);
                } else {
                    parentMenu.onSelect(info);
                }
            } else if (!selected) {
                parentMenu.onSelect(info);
            }
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onMouseEnter(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }

        const /** @type {?} */ parentMenu: Menu = this.parentMenu;
        this.clearMouseLeaveTimer();

        // first is mouse-enter
        parentMenu.onMouseEnter({
            key: this.eventKey,
            item: this,
            domEvent: event,
            trigger: 'mouseenter'
        });

        // and then is item-hover
        parentMenu.onItemHover({
            key: this.eventKey,
            item: this,
            hover: true,
            domEvent: event,
            trigger: 'mouseenter'
        });
    }
/**
 * @param {?} event
 * @return {?}
 */
onMouseLeave(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }

        const /** @type {?} */ parentMenu: Menu = this.parentMenu;
        this.mouseLeaveTimer = setTimeout(() => {
            // we will trigger hover=false event later
            if (this.active) {
                parentMenu.onItemHover({
                    key: this.eventKey,
                    item: this,
                    hover: false,
                    domEvent: event,
                    trigger: 'mouseleave'
                });
            }
        }, 30);

        parentMenu.onMouseLeave({
            key: this.eventKey,
            item: this,
            domEvent: event,
            trigger: 'mouseleave'
        });
    }
/**
 * @param {?} event
 * @return {?}
 */
onKeyDown(event: KeyboardEvent): void {
        const /** @type {?} */ keyCode = event.keyCode;
        if (keyCode === KeyCode.ENTER) {
            this.onClick(event);
        }
    }
/**
 * The underlying host native element
 * @return {?}
 */
getHostElement(): HTMLElement {
        return /** @type {?} */(( this.elementRef.nativeElement as HTMLElement));
    }
/**
 * @return {?}
 */
getKeyPath(): string[] {
        const /** @type {?} */ keyPath: string[] = this.submenu && this.submenu.getKeyPath() || [];
        return keyPath.concat(this.eventKey);
    }
/**
 * @return {?}
 */
isSelected() {
        return this.parentMenu && this.parentMenu.selectedKeys.indexOf(this.eventKey) !== -1;
    }
/**
 * @return {?}
 */
clearMouseLeaveTimer(): void {
        if (this.mouseLeaveTimer) {
            clearTimeout(this.mouseLeaveTimer);
            this.mouseLeaveTimer = null;
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-menu-item',
    template: '<ng-content></ng-content>',
    encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
{type: Menu, decorators: [{ type: Inject, args: [forwardRef(() => Menu), ] }, { type: Optional }, { type: Host }, ]},
{type: SubMenu, decorators: [{ type: Inject, args: [forwardRef(() => SubMenu), ] }, { type: Optional }, { type: Host }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'title': [{ type: Input },],
'eventKey': [{ type: Input, args: ['key', ] },],
'disabled': [{ type: Input },],
'multiple': [{ type: Input },],
'inlineIndent': [{ type: Input },],
'class': [{ type: Input },],
'openAnimation': [{ type: Input },],
'openTransitionName': [{ type: Input },],
'role': [{ type: HostBinding, args: ['attr.role', ] },],
'classes': [{ type: HostBinding, args: ['class', ] },],
'paddingLeft': [{ type: HostBinding, args: ['style.paddingLeft', ] },],
'ariaselected': [{ type: HostBinding, args: ['attr.aria-selected', ] },],
'ariadisabled': [{ type: HostBinding, args: ['attr.aria-disabled', ] },],
'onClick': [{ type: HostListener, args: ['click', ['$event'], ] },],
'onMouseEnter': [{ type: HostListener, args: ['mouseenter', ['$event'], ] },],
'onMouseLeave': [{ type: HostListener, args: ['mouseleave', ['$event'], ] },],
'onKeyDown': [{ type: HostListener, args: ['keydown', ['$event'], ] },],
};
}

function MenuItem_tsickle_Closure_declarations() {
/** @type {?} */
MenuItem.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MenuItem.ctorParameters;
/** @type {?} */
MenuItem.propDecorators;
/** @type {?} */
MenuItem.prototype.isMenuItem;
/** @type {?} */
MenuItem.prototype.isSubMenu;
/** @type {?} */
MenuItem.prototype.title;
/** @type {?} */
MenuItem.prototype.eventKey;
/** @type {?} */
MenuItem.prototype.disabled;
/**
 * 是否允许多选，默认为false
 * @type {?}
 */
MenuItem.prototype.multiple;
/**
 * 模式的菜单缩进宽度，默认为24px
 * @type {?}
 */
MenuItem.prototype.inlineIndent;
/**
 * 用户CSS样式
 * @type {?}
 */
MenuItem.prototype.class;
/**
 * 展开/关闭菜单时的动画效果
 * @type {?}
 */
MenuItem.prototype.openAnimation;
/** @type {?} */
MenuItem.prototype.openTransitionName;
/** @type {?} */
MenuItem.prototype.role;
/** @type {?} */
MenuItem.prototype.open;
/** @type {?} */
MenuItem.prototype.level;
/**
 * 鼠标移出的一定时间后(如30毫秒)，再执行隐藏子菜单的操作
 * @type {?}
 */
MenuItem.prototype.mouseLeaveTimer;
/** @type {?} */
MenuItem.prototype.renderer;
/** @type {?} */
MenuItem.prototype.elementRef;
/** @type {?} */
MenuItem.prototype.parentMenu;
/** @type {?} */
MenuItem.prototype.submenu;
}



interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
